import React, { useState, useEffect, useCallback } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { Search, ChevronLeft, ChevronRight } from 'lucide-react'
import { useForm, Controller } from 'react-hook-form'
import * as Yup from 'yup'

import { Group } from '@/core/api/entities/group'
import { Zone } from '@/core/api/entities/zone/zone'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useZones } from '@/core/hooks/useZone'
import { useDebounce } from '@/core/hooks/utils/useDebounce'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { ErrorMessage } from '@/shared/components/atoms/ErrorMessage'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import { ScrollArea } from '@/shared/components/atoms/Scroll'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	BottomSheet,
	BottomSheetContent,
	BottomSheetTitle,
} from '@/shared/components/molecules/BottomSheet/BottomSheet'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { ZoneRow } from '@/shared/components/molecules/ZoneRow/ZoneRow'
import { cn } from '@/shared/utils/helpers'

const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 50

interface AddZoneToGroupModalProps {
	visible: boolean
	group: Group | null
	onClose: () => void
	onConfirm: (selectedZones: Zone[], groupName?: string) => void
	isAdding: boolean
	mode?: 'edit' | 'create'
}

const AddZoneToGroupModal: React.FC<AddZoneToGroupModalProps> = ({
	visible,
	group,
	onClose,
	onConfirm,
	isAdding,
	mode = 'edit',
}) => {
	const { t } = useTranslation('common')
	const [searchQuery, setSearchQuery] = useState('')

	// Store selected zones as full objects to preserve them across pagination
	const [selectedZones, setSelectedZones] = useState<Zone[]>([])

	// Pagination state
	const [page, setPage] = useState(DEFAULT_PAGE)
	const pageSize = DEFAULT_PAGE_SIZE
	const [isDesktop, setIsDesktop] = useState(
		() => !window.matchMedia('(max-width: 1023px)').matches
	)

	// Debounce search query for server-side search
	const debouncedSearchQuery = useDebounce(searchQuery, 500)

	const isCreateMode = mode === 'create'
	const modalTitle = isCreateMode
		? t('entities.group.create.step1.title')
		: t('entities.group.add_zones.title')

	const validationSchema = Yup.object().shape({
		name: isCreateMode
			? Yup.string()
					.required(t('validation.required'))
					.min(3, t('validation.minLength', { min: 3 }))
					.max(255, t('validation.maxLength', { max: 255 }))
			: Yup.string(),
	})

	const form = useForm({
		resolver: yupResolver(validationSchema),
		defaultValues: {
			name: '',
		},
	})

	const {
		control,
		formState: { errors },
		getValues,
		reset: resetForm,
	} = form

	// Fetch zones with pagination and search
	const { data: zonesResponse, isLoading } = useZones({
		config: useAccountConfig({
			params: {
				page,
				per_page: pageSize,
				query: debouncedSearchQuery || undefined,
				include: 'store.lastGooglePlaceData',
			},
		}),
		options: {
			enabled: visible,
		},
	})

	const zones = zonesResponse?.data || []
	const paginationMeta = zonesResponse?.meta?.pagination

	// Reset page to 1 when search query changes
	useEffect(() => {
		setPage(DEFAULT_PAGE)
	}, [debouncedSearchQuery])

	useEffect(() => {
		const mediaQuery = window.matchMedia('(max-width: 1023px)')
		const updateDesktopState = () => setIsDesktop(!mediaQuery.matches)
		updateDesktopState()

		mediaQuery.addEventListener('change', updateDesktopState)

		return () => mediaQuery.removeEventListener('change', updateDesktopState)
	}, [])

	// Pre-select zones that the group already has when modal opens
	useEffect(() => {
		if (visible && group?.zones) {
			setSelectedZones(group.zones)
		}
		if (!visible) {
			resetForm()
			setSearchQuery('')
			setPage(DEFAULT_PAGE)
		}
	}, [group?.zones, visible, resetForm])

	// Derive selectedZoneIds from selectedZones
	const selectedZoneIds = selectedZones.map((z) => z.id)

	// Handle pagination changes
	const onPageChange = useCallback((newPage: number) => {
		setPage(newPage)
	}, [])

	// Handle zone selection toggle
	const handleZoneToggle = (zone: Zone) => {
		const zoneId = zone.id
		if (selectedZoneIds.includes(zoneId)) {
			setSelectedZones((prev) => prev.filter((z) => z.id !== zoneId))
		} else {
			setSelectedZones((prev) => [...prev, zone])
		}
	}

	// Handle confirm
	const handleConfirm = () => {
		if (isCreateMode && errors.name) {
			return
		}
		const groupName = isCreateMode ? getValues('name') : undefined
		onConfirm(selectedZones, groupName)
	}

	// Handle close
	const handleClose = () => {
		if (isAdding) return // Prevent closing while adding
		onClose()
		setSelectedZones([])
		setSearchQuery('')
		setPage(DEFAULT_PAGE)
		resetForm()
	}

	// Calculate selected count for display
	const selectedCount = selectedZoneIds.length

	const renderModalBody = () => (
		<>
			<div className="px-6 py-3 flex flex-col flex-1 min-h-0 overflow-hidden">
				{isCreateMode && (
					<div className="mb-4 flex-none">
						<Label className="mb-1 block">
							{t('entities.group.create.step1.name.label')}
						</Label>
						<Controller
							name="name"
							control={control}
							render={({ field }) => (
								<div>
									<Input
										{...field}
										placeholder={t(
											'entities.group.create.step1.name.placeholder'
										)}
										disabled={isAdding}
										className="h-10 text-sm"
									/>
									{errors.name && (
										<ErrorMessage error={errors.name.message ?? ''} />
									)}
								</div>
							)}
						/>
					</div>
				)}
				{isCreateMode && (
					<Label className="mb-1 block flex-none">
						{t('entities.group.create.step1.name.select_zones')}
					</Label>
				)}
				<div className="relative flex-none mb-3">
					<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t('entities.group.add_zones.search_zone_placeholder')}
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="pl-10"
					/>
				</div>

				<div className="flex items-center justify-between flex-none mb-3">
					<span className="text-sm text-muted-foreground">
						{t('entities.group.add_zones.selected_zones_count', {
							count: selectedCount,
						})}
					</span>
				</div>

				<ScrollArea className="flex-1 min-h-0 border rounded-md overflow-y-auto">
					{isLoading ? (
						<div className="flex items-center justify-center h-full text-muted-foreground text-sm pt-3">
							{t('entities.group.add_zones.loading')}
						</div>
					) : zones.length === 0 ? (
						<div className="flex items-center justify-center h-full text-muted-foreground text-sm pt-3">
							{searchQuery
								? t('entities.group.add_zones.no_zones_found')
								: t('entities.group.add_zones.no_zones_available')}
						</div>
					) : (
						<div className="divide-y pb-2">
							{zones.map((zone) => {
								const isSelected = selectedZoneIds.includes(zone.id)

								return (
									<ZoneRow
										key={`zone-${zone.id}`}
										zone={zone}
										isSelected={isSelected}
										onSelectionChange={() => handleZoneToggle(zone)}
										showAddress={true}
										className={cn(isSelected ? 'bg-shade' : '')}
									/>
								)
							})}
						</div>
					)}
				</ScrollArea>

				{/* Compact Pagination */}
				{(paginationMeta?.total_pages ?? 1) > 1 && (
					<div className="flex items-center justify-between pt-2">
						<span className="text-xs text-muted-foreground">
							{page} / {paginationMeta?.total_pages ?? 1}
						</span>
						<div className="flex items-center gap-1">
							<Button
								variant="ghost"
								size="icon"
								className="h-7 w-7"
								onClick={() => onPageChange(page - 1)}
								disabled={page === 1}
							>
								<ChevronLeft className="h-4 w-4" />
							</Button>
							<Button
								variant="ghost"
								size="icon"
								className="h-7 w-7"
								onClick={() => onPageChange(page + 1)}
								disabled={page >= (paginationMeta?.total_pages ?? 1)}
							>
								<ChevronRight className="h-4 w-4" />
							</Button>
						</div>
					</div>
				)}
			</div>

			<Separator />

			<DialogFooter className="px-6 py-4">
				<Button
					type="button"
					variant="outline"
					onClick={handleClose}
					disabled={isAdding}
					className="h-9 rounded-full px-6"
				>
					{t('actions.cancel')}
				</Button>
				<Button
					type="button"
					onClick={handleConfirm}
					disabled={isAdding || selectedZones.length === 0}
					className="h-9 rounded-full px-6"
				>
					{isAdding
						? t('actions.loading')
						: isCreateMode
							? t('actions.continue')
							: t('entities.group.add_zones.assign_zones_action', {
									count: selectedCount,
								})}
				</Button>
			</DialogFooter>
		</>
	)

	return isDesktop ? (
		<Dialog open={visible} onOpenChange={handleClose}>
			<DialogContent className="max-w-3xl p-0 gap-0 flex flex-col max-h-[90vh] overflow-hidden">
				<DialogHeader className="px-6 pt-5 pb-3">
					<DialogTitle className="text-xl">{modalTitle}</DialogTitle>
				</DialogHeader>
				<Separator />
				{renderModalBody()}
			</DialogContent>
		</Dialog>
	) : (
		<BottomSheet open={visible} onOpenChange={handleClose}>
			<BottomSheetContent
				variant="card"
				className="h-[92dvh] max-h-[92dvh] p-0 gap-0 flex flex-col"
			>
				<div className="px-6 pt-2 pb-3">
					<BottomSheetTitle className="text-xl">{modalTitle}</BottomSheetTitle>
				</div>
				<Separator />
				{renderModalBody()}
			</BottomSheetContent>
		</BottomSheet>
	)
}

export default AddZoneToGroupModal
